import { carsApi } from "@apac/cars";
import {
  type Itinerary,
  ItineraryState,
  type SingleItineraryRequest,
  type SingleItineraryResponse,
} from "@b2bportal/ground-api/lib/api";
import { Product } from "@b2bportal/purchase-api";
import type { ReservationWrapper } from "@overrides/types";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import dayjs from "dayjs";

export interface CarReservationWrapper extends ReservationWrapper<Itinerary> {
  endDate?: string;
}

export interface ICarItinerariesResponse {
  [key: string]: Array<CarReservationWrapper>;
}

export const carsPostBookingApi = createApi({
  reducerPath: "carsPostBooking",
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
  }),
  tagTypes: ["itineraries"],
  endpoints: (builder) => ({
    fetchCarItineraries: builder.query<ICarItinerariesResponse, void>({
      keepUnusedDataFor: 180,
      queryFn: async () => {
        const response = await carsApi.apiV1GroundItinerariesPut({
          states: [
            ItineraryState.Canceled,
            ItineraryState.Future,
            ItineraryState.Present,
            ItineraryState.Past,
          ],
          referenceDateTime: dayjs().format(),
        });
        const wrappedCarReservations: ICarItinerariesResponse = Object.keys(
          response.data.itinerariesByState
        ).reduce((acc, key) => {
          const reservations = response.data.itinerariesByState[key];
          const wrapped = reservations.map((reservation) => ({
            reservation,
            reservationId: reservation.reservation.internalGroundBookingId,
            sortDate: reservation.reservation.pickUp.dateTime,
            // Add endDate for secondary sorting when pickup dates are the same
            endDate: reservation.reservation.dropOff.dateTime,
            recentFirst:
              key === ItineraryState.Past || key === ItineraryState.Canceled,
            isCancelled: key === ItineraryState.Canceled,
            productType: Product.Ground,
          }));
          return { ...acc, [key]: wrapped };
        }, {});
        return {
          data: wrappedCarReservations,
        };
      },
      providesTags: ["itineraries"],
    }),
    fetchCarItinerary: builder.query<
      SingleItineraryResponse,
      SingleItineraryRequest
    >({
      queryFn: async (request) => {
        const response = await carsApi.apiV1GroundItinerariesSinglePut(request);
        return { data: response.data };
      },
    }),
  }),
});

export const { useFetchCarItinerariesQuery, useFetchCarItineraryQuery } =
  carsPostBookingApi;
